import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import Mail01Icon from "@redotech/redo-icons/mail-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useEnabledIntegrations } from "@redotech/redo-merchant-marketing-app/automations/use-enabled-integrations";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import {
  ORIGIN_RETURN_TRACKING_TEAM_ID,
  SendEmailStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { getVisibleDynamicVariables } from "@redotech/redo-model/advanced-flow/schemas/filter-schema-fields";
import { schemas } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { TemplateType } from "@redotech/redo-model/email-builder";
import { getDefaultTemplate } from "@redotech/redo-model/email-template-defaults";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { EmailTextInputToolbar } from "@redotech/redo-web/email-builder/builder/text-input-toolbar";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { AiEmailPreviewSuggestions } from "@redotech/redo-web/marketing/components/ai-email-preview-suggestions";
import { AiSubjectLineSuggestions } from "@redotech/redo-web/marketing/components/ai-subject-line-suggestions";
import { Text } from "@redotech/redo-web/text";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import { InvalidStepsList } from "../components/invalid-steps-list";
import { AutomationAbTestEditor } from "./automation-ab-test-editor";
import { AutomationAbTestInspector } from "./automation-ab-test-inspector";
import { AutomationAbTestResultsPanel } from "./automation-ab-test-results-panel";
import { EmailPreviewContent } from "./common/email-preview-content";
import { useAbTestEditor } from "./common/use-ab-test-editor";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";
import { VariantPlaceholder } from "./common/variant-placeholders";

type Props = StepTypeDetailsProps<PartialWithRequiredType<SendEmailStep>>;

interface EmailDraft {
  emailName: string;
  subject: string;
  previewText: string;
}

/**
 * Module-level cache to persist draft email step state (emailName, subject,
 * previewText) by step ID. These fields live in local component state and
 * would otherwise be lost if the component remounts when navigating between
 * blocks of the same type.
 */
const emailDraftCache = new Map<string, EmailDraft>();

export function SendEmailTemplatePreview({
  step,
  schemaType,
  category,
  setStep,
  save,
  invalidSteps,
  setSelectedId,
  showValidationErrors,
  setAutomation,
  automationId,
  steps,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const redoMerchantRpcClient = useRequiredContext(
    RedoMerchantRpcClientContext,
  );
  const otherInvalidSteps = invalidSteps?.filter((s) => s.id !== step.id) || [];
  const hasOtherInvalidSteps = !!otherInvalidSteps.length;

  // For advanced flow editor, we don't filter by integrations to keep all fields available
  const enabledIntegrations = useEnabledIntegrations();
  const availableDynamicVariables = useMemo(() => {
    const schema = schemaType ? schemas[schemaType] : undefined;
    return getVisibleDynamicVariables(schema, enabledIntegrations);
  }, [schemaType, enabledIntegrations]);

  const isOriginFlow = useMemo(() => {
    return location.pathname.includes("/managed/");
  }, [location.pathname]);

  const onOriginTeam = useMemo(() => {
    return team._id.toString() === ORIGIN_RETURN_TRACKING_TEAM_ID;
  }, [team]);

  const [emailName, setEmailName] = useState<string>(
    () => emailDraftCache.get(step.id)?.emailName ?? "",
  );
  const [subject, setSubject] = useState<string>(
    () => emailDraftCache.get(step.id)?.subject ?? "",
  );
  const [previewText, setPreviewText] = useState<string>(
    () => emailDraftCache.get(step.id)?.previewText ?? "",
  );
  const subjectLineRef = useRef(null);
  const previewTextRef = useRef(null);
  const [showFullEmailDialog, setShowFullEmailDialog] =
    useState<boolean>(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [emailNameTouched, setEmailNameTouched] = useState<boolean>(
    (showValidationErrors && !hasOtherInvalidSteps) || false,
  );
  const [emailTemplateReloadTrigger, setEmailTemplateReloadTrigger] =
    useState(Symbol());

  const delayedAutoFocus = useDelayedAutoFocus(!hasOtherInvalidSteps);
  const delayedAutoFocusInvalidSteps =
    useDelayedAutoFocus(hasOtherInvalidSteps);

  // Variant modal extra fields (email-specific)
  const [variantSubject, setVariantSubject] = useState("");
  const [variantPreviewText, setVariantPreviewText] = useState("");

  // Get the existing template and determine if it exists.
  // Return `undefined` (not null) when there is no templateId so that a
  // stale `undefined` value from a previous step with no template does NOT
  // satisfy the `=== null` "deleted" check in templateState below.
  const emailTemplateLoad = useLoad(
    async (signal) => {
      if (!step.templateId && !step.originTemplateId) {
        return undefined;
      }
      try {
        const input = step.templateId
          ? { id: step.templateId }
          : { originTemplateId: step.originTemplateId! };
        return await marketingRpcClient.getEmailTemplate(input, { signal });
      } catch {
        return null;
      }
    },
    [step.templateId, step.originTemplateId, emailTemplateReloadTrigger],
  );

  const templateState = useMemo(() => {
    if (step.originTemplateId && !step.templateId && emailTemplateLoad.value) {
      const isOriginTemplate =
        emailTemplateLoad.value.isOrigin && !onOriginTeam;
      return {
        state: isOriginTemplate ? ("origin" as const) : ("exists" as const),
        template: emailTemplateLoad.value,
      };
    }
    if (step.templateId && emailTemplateLoad.value) {
      return { state: "exists" as const, template: emailTemplateLoad.value };
    }
    if (
      (step.templateId || step.originTemplateId) &&
      emailTemplateLoad.value === null
    ) {
      return { state: "deleted" as const };
    }
    return { state: "create" as const };
  }, [
    step.templateId,
    step.originTemplateId,
    emailTemplateLoad.value,
    onOriginTeam,
  ]);

  // Create template function for A/B test variants
  const createVariantTemplate = async (name: string) => {
    const newTemplate = getDefaultTemplate({
      team,
      name,
      subject: variantSubject,
      emailPreview: variantPreviewText,
      type: TemplateType.MARKETING,
      schemaType,
      category,
    });
    const response = await marketingRpcClient.createEmailTemplate(newTemplate);
    return response._id.toString();
  };

  // A/B test editor hook
  const abTest = useAbTestEditor({
    automationId,
    stepId: step.id,
    stepType: "send_email",
    splitId: step.splitId,
    existingTemplateId: step.templateId,
    existingTemplateName:
      templateState.state === "exists" || templateState.state === "origin"
        ? templateState.template.name
        : undefined,
    onSplitIdChange: (splitId) => setStep({ ...step, splitId }),
    onSave: save,
    createTemplate: createVariantTemplate,
    getEditPath: (templateId, returnPath) =>
      `../../templates/email/${templateId}?${returnPath}&showEmailReferencesModal=true`,
  });

  const renderVariantPreview = (treatment: Treatment) => {
    if (!treatment.templateId) {
      return <VariantPlaceholder hasTemplate={false} type="template" />;
    }
    return <EmailPreviewContent templateId={treatment.templateId} />;
  };

  const renderFullVariantPreview = (treatment: Treatment) => {
    if (!treatment.templateId) {
      return (
        <Flex align="center" justify="center" p="lg" w="full">
          <Text fontSize="sm" textAlign="center" textColor="tertiary">
            No template selected
          </Text>
        </Flex>
      );
    }
    return (
      <EmailPreviewContent fullPreview templateId={treatment.templateId} />
    );
  };

  // Handle opening variant modal - reset extra fields
  const handleOpenVariantModal = () => {
    setVariantSubject("");
    setVariantPreviewText("");
    const variantLetter = String.fromCharCode(
      65 + (abTest.splitWithTreatments?.treatments.length ?? 1),
    );
    const variantName = `Variant ${variantLetter}`;
    abTest.variantModal.open(variantName, `${variantName} Email`);
  };

  const emailNameError = useMemo(() => {
    if (
      templateState.state !== "exists" &&
      templateState.state !== "origin" &&
      !emailName &&
      emailNameTouched
    ) {
      return "Email name is required";
    }
    return undefined;
  }, [emailName, templateState.state, emailNameTouched]);

  // When the selected step changes (step.id), save the current draft to the
  // cache so it can be restored if this step is selected again.
  useEffect(
    () =>
      function setDraftCacheOnStepChange() {
        // Save draft only while the step is still in "create" mode.
        // Once a template exists the draft fields are irrelevant.
        if (!step.templateId && !step.originTemplateId) {
          emailDraftCache.set(step.id, { emailName, subject, previewText });
        } else {
          emailDraftCache.delete(step.id);
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step.id],
  );

  useEffect(
    function removeIdOfDeletedTemplate() {
      if (templateState.state === "deleted") {
        setStep({ ...step, templateId: undefined });
      }
    },
    [templateState, setStep, step],
  );

  const handleCreateTemplateClick = async () => {
    try {
      const newTemplate = getDefaultTemplate({
        team,
        name: emailName,
        subject,
        emailPreview: previewText,
        type: TemplateType.MARKETING,
        schemaType,
        category,
      });

      const response =
        await marketingRpcClient.createEmailTemplate(newTemplate);

      const updatedStep = {
        ...step,
        templateId: response._id.toString(),
        originTemplateId: undefined,
      };

      if (isOriginFlow && automationId && setAutomation) {
        const currentFlowData = await redoMerchantRpcClient.getAdvancedFlowById(
          { flowId: automationId },
        );

        if (!currentFlowData.advancedFlow) {
          throw new Error("Origin flow not loaded");
        }

        const { _id, createdAt, updatedAt, versionGroupId, ...flowData } =
          currentFlowData.advancedFlow;

        const updatedSteps = steps.map((s) =>
          s.id === step.id ? updatedStep : s,
        );

        const newFlowId = await redoMerchantRpcClient.createAdvancedFlow({
          newFlow: { ...flowData, steps: updatedSteps as any, team: team._id },
          setIndex: true,
        });

        setAutomation((prev) =>
          prev ? { ...prev, _id: newFlowId.id, steps: updatedSteps } : prev,
        );

        const newPath = location.pathname.replace(
          `/managed/${automationId}`,
          `/${newFlowId.id}`,
        );

        navigate(
          `../../templates/email/${response._id.toString()}?returnPath=${encodeURIComponent(newPath)}&selected=${step.id}&showEmailReferencesModal=true`,
          { replace: true },
        );

        // Draft is no longer needed once the template is created.
        emailDraftCache.delete(step.id);

        toast("Flow customized and saved to your automations", {
          variant: "success",
        });
      } else {
        setStep(updatedStep);
        save?.();
        // Draft is no longer needed once the template is created.
        emailDraftCache.delete(step.id);

        navigate(
          `../../templates/email/${response._id.toString()}?returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}&showEmailReferencesModal=true`,
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast(`Error creating template: ${message}`, { variant: "error" });
    }
  };

  const handleRevertToManagedEmail = async () => {
    if (!step.originTemplateId) {
      toast("No origin template found", { variant: "error" });
      return;
    }

    try {
      await marketingRpcClient.deleteOriginTemplateFromTeam({
        originTemplateId: step.originTemplateId,
      });
      setShowRevertModal(false);
      setEmailTemplateReloadTrigger(Symbol());

      toast("Reverted to default email template", { variant: "success" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast(`Error reverting to default email: ${message}`, {
        variant: "error",
      });
    }
  };

  const handleEditTemplate = () => {
    const returnPath = `returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}`;
    const isOriginTemplate = templateState.state === "origin";

    if (isOriginTemplate) {
      if (step.originTemplateId) {
        navigate(
          `../../templates/email/managed/${step.originTemplateId}?${returnPath}`,
        );
      }
    } else {
      const templateIdToEdit =
        step.templateId ||
        (templateState.state === "exists"
          ? templateState.template._id.toString()
          : undefined);
      if (templateIdToEdit) {
        navigate(`../../templates/email/${templateIdToEdit}?${returnPath}`);
      }
    }
  };

  const handleSelectWinner = (winningTemplateId: string) => {
    setStep({
      ...step,
      templateId: winningTemplateId,
      originTemplateId: undefined,
      splitId: undefined,
    });
    save?.();
    abTest.refetch();
  };

  // Render based on A/B test view
  if (abTest.view === "loading") {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (abTest.view === "results" && automationId) {
    return (
      <AutomationAbTestResultsPanel
        automationId={automationId}
        automationStepId={step.id}
        icon={<Mail01Icon height={20} width={20} />}
        onSelectWinner={handleSelectWinner}
        renderFullVariantPreview={renderFullVariantPreview}
        renderVariantPreview={renderVariantPreview}
        stepType="send_email"
        title="Send email A/B test"
      />
    );
  }

  if (abTest.view === "editor" && abTest.splitWithTreatments) {
    return (
      <>
        <AutomationAbTestEditor
          icon={<Mail01Icon height={20} width={20} />}
          onAddVariant={handleOpenVariantModal}
          onClose={abTest.closeEditor}
          onEditVariant={abTest.editVariant}
          renderVariantPreview={renderVariantPreview}
          splitWithTreatments={abTest.splitWithTreatments}
          stepType="send_email"
          title="Send email A/B test"
        />

        {/* Variant template creation modal */}
        <RedoModal
          isOpen={abTest.variantModal.isOpen}
          modalSize="md"
          onModalCloseRequested={abTest.variantModal.close}
          primaryButton={{
            text: "Create Template",
            onClick: abTest.variantModal.create,
            disabled: !abTest.variantModal.templateName,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: abTest.variantModal.close,
          }}
          title={`Create template for ${abTest.variantModal.variantName}`}
        >
          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                Email name
              </Text>
              <RedoTextInput
                placeholder="Enter email name"
                setValue={abTest.variantModal.setTemplateName}
                size="sm"
                value={abTest.variantModal.templateName}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                Subject line
              </Text>
              <RedoTextInput
                placeholder="Enter subject line"
                setValue={setVariantSubject}
                size="sm"
                value={variantSubject}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                Preview text
              </Text>
              <RedoTextInput
                placeholder="Enter preview text"
                setValue={setVariantPreviewText}
                size="sm"
                value={variantPreviewText}
              />
            </Flex>
          </Flex>
        </RedoModal>
      </>
    );
  }

  // Loading states for template data
  if (emailTemplateLoad.pending) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  const isReadOnly =
    templateState.state === "exists" || templateState.state === "origin";
  const isOriginTemplate = templateState.state === "origin";
  const template =
    templateState.state === "exists" || templateState.state === "origin"
      ? templateState.template
      : undefined;

  const hasTemplate = !!step.templateId || !!step.originTemplateId;

  // Normal inspector view
  return (
    <InspectorLayout title="Send email">
      <InspectorSection label="Email">
        <InspectorField label="Email name">
          <Flex dir="column" gap="xs" w="full">
            <Flex align="center" gap="sm" justify="space-between" w="full">
              <Flex grow={1}>
                <RedoTextInput
                  autoFocus={delayedAutoFocus}
                  errorText={emailNameError}
                  key={delayedAutoFocus.toString()}
                  onBlur={() => setEmailNameTouched(true)}
                  placeholder={isReadOnly ? "No name" : "Add a name"}
                  setValue={setEmailName}
                  size="sm"
                  state={
                    isReadOnly || hasOtherInvalidSteps ? "readonly" : "default"
                  }
                  value={template?.name || emailName}
                />
              </Flex>
              {isOriginTemplate && (
                <RedoBadge color="gray" size="xs" text="Default template" />
              )}
            </Flex>
          </Flex>
        </InspectorField>

        <InspectorField label="Subject">
          <Flex dir="column" gap="xs" w="full">
            <RedoTextareaInputField
              autoExpand
              placeholder={isReadOnly ? "No subject line" : "Add a subject"}
              readOnly={isReadOnly || hasOtherInvalidSteps}
              ref={subjectLineRef}
              setValue={setSubject}
              toolbar={
                <EmailTextInputToolbar
                  availableDynamicVariables={availableDynamicVariables}
                  inputRef={subjectLineRef}
                  schemaType={schemaType}
                  setText={setSubject}
                  text={subject}
                />
              }
              value={template?.subject || subject}
            />
            {!isReadOnly && !hasOtherInvalidSteps && (
              <AiSubjectLineSuggestions
                getAiSubjectLineSuggestions={
                  marketingRpcClient.getAiSubjectLineSuggestions
                }
                onSuggestionClick={setSubject}
                subject={subject}
              />
            )}
          </Flex>
        </InspectorField>

        <InspectorField label="Preview">
          <Flex dir="column" gap="xs" w="full">
            <RedoTextareaInputField
              autoExpand
              placeholder={isReadOnly ? "No preview text" : "Add a preview"}
              readOnly={isReadOnly || hasOtherInvalidSteps}
              ref={previewTextRef}
              setValue={setPreviewText}
              toolbar={
                <EmailTextInputToolbar
                  availableDynamicVariables={availableDynamicVariables}
                  inputRef={previewTextRef}
                  schemaType={schemaType}
                  setText={setPreviewText}
                  text={previewText}
                />
              }
              value={template?.emailPreview || previewText}
            />
            {!isReadOnly && !hasOtherInvalidSteps && (
              <AiEmailPreviewSuggestions
                emailPreview={previewText}
                getAiEmailPreviewSuggestions={
                  marketingRpcClient.getAiEmailPreviewSuggestions
                }
                onSuggestionClick={setPreviewText}
                subjectLine={subject}
              />
            )}
          </Flex>
        </InspectorField>

        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" textColor="secondary">
              Content
            </Text>
            {isReadOnly && (
              <RedoButton
                disabled={hasOtherInvalidSteps}
                hierarchy="secondary"
                IconLeading={EditPencilIcon}
                onClick={handleEditTemplate}
                size="xs"
                text={isOriginTemplate ? "Customize" : "Edit"}
              />
            )}
          </Flex>

          <Flex
            bgColor="primary"
            borderColor={
              !isReadOnly &&
              !(step.templateId || step.originTemplateId) &&
              showValidationErrors
                ? "error"
                : "primary"
            }
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            p="sm"
            position="relative"
            radius="sm"
            style={{ maxHeight: "400px" }}
            w="full"
          >
            {hasOtherInvalidSteps && setSelectedId ? (
              <InvalidStepsList
                autoFocus={delayedAutoFocusInvalidSteps}
                invalidSteps={otherInvalidSteps}
                key={delayedAutoFocusInvalidSteps.toString()}
                onGoToStep={setSelectedId}
              />
            ) : step.templateId || step.originTemplateId ? (
              <EmailPreviewContent
                onClick={() => setShowFullEmailDialog(true)}
                originTemplateId={step.originTemplateId}
                templateId={step.templateId}
              />
            ) : isReadOnly ? (
              <Flex
                align="center"
                bgColor="primary"
                height="half-screen"
                justify="center"
              >
                <Text fontSize="xs" textAlign="center" textColor="tertiary">
                  Could not generate preview
                </Text>
              </Flex>
            ) : (
              <Flex
                align="center"
                bgColor="primary"
                height="half-screen"
                justify="center"
              >
                <RedoButton
                  disabled={!emailName}
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={handleCreateTemplateClick}
                  text="Content"
                  theme="action"
                />
              </Flex>
            )}
          </Flex>

          {!isReadOnly &&
            !(step.templateId || step.originTemplateId) &&
            showValidationErrors && (
              <Text fontSize="xs" textColor="error">
                {hasOtherInvalidSteps
                  ? `Fix invalid ${otherInvalidSteps.length > 1 ? "steps" : "step"} before adding content`
                  : !emailName
                    ? "Add email name before adding content"
                    : "Content is required"}
              </Text>
            )}
        </Flex>
      </InspectorSection>

      <InspectorSection label="Step">
        <InspectorField label={null}>
          <RedoToggle
            label="Deactivate"
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>

        {step.originTemplateId && !isOriginTemplate && !onOriginTeam && (
          <RedoButton
            disabled={hasOtherInvalidSteps}
            hierarchy="secondary"
            onClick={() => setShowRevertModal(true)}
            size="sm"
            text="Revert to default email"
            theme="destructive"
          />
        )}
      </InspectorSection>

      {/* A/B test panel */}
      {automationId && (
        <AutomationAbTestInspector
          existingTest={abTest.splitWithTreatments}
          hasTemplate={hasTemplate}
          onOpenEditor={abTest.openEditor}
        />
      )}

      {showFullEmailDialog && (step.templateId || step.originTemplateId) && (
        <RedoModal
          isOpen={showFullEmailDialog}
          modalSize="lg"
          onModalCloseRequested={() => setShowFullEmailDialog(false)}
          primaryButton={{
            text: isOriginTemplate ? "Customize" : "Edit",
            onClick: handleEditTemplate,
          }}
          secondaryButton={{
            text: "Close",
            onClick: () => setShowFullEmailDialog(false),
          }}
          title="Email Preview"
        >
          <Flex
            dir="column"
            p="lg"
            style={{ maxHeight: "70vh", overflow: "auto" }}
          >
            <EmailPreviewContent
              fullPreview
              originTemplateId={step.originTemplateId}
              templateId={step.templateId}
            />
          </Flex>
        </RedoModal>
      )}

      {showRevertModal && (
        <RedoModal
          isOpen={showRevertModal}
          onModalCloseRequested={() => setShowRevertModal(false)}
          primaryButton={{
            text: "Revert",
            onClick: handleRevertToManagedEmail,
            theme: "destructive",
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setShowRevertModal(false),
          }}
          theme="error"
          title="Revert to default email?"
        >
          <Flex dir="column" gap="md">
            <Text fontSize="sm" textColor="secondary">
              This will discard your custom email template and revert to the
              default version. This action cannot be undone.
            </Text>
          </Flex>
        </RedoModal>
      )}
    </InspectorLayout>
  );
}
